import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { AppRoutes } from '@/utils/types';
import styled from '@emotion/styled';
import { IconButton } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { ElementType } from 'polotno/model/group-model';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FigmaModal } from '../panels/templates/FigmaImport/FigmaModal';
import { CropDropdown } from './CropDropdown';
import { VideoTitleInput } from './VideoTitleInput';

const Wrapper = styled.div`
    display: flex;
    background: #fff;
    padding: 16px 20px;
    height: 72px;
    align-items: center;
    gap: 24px;
`;

const StyledCircularProgress = styled(CircularProgress)`
    color: ${({ theme }) => theme.colors.black[700]};
    margin: 12px;
`;

const HeaderWrapper = styled.div`
    margin-left: 25px;
    display: flex;
    align-items: center;
`;

const VerticalDivider = styled.div`
    margin-left: 24px;
    margin-right: 12px;
    width: 1px;
    background-color: ${({ theme }) => theme.colors.black[300]};
    height: 24px;
`;

const StyledMaskedIcon = styled(MaskedIcon)<{ isDisabled?: boolean }>`
    color: ${({ theme, isDisabled }) => (isDisabled ? theme.colors.black[400] : theme.colors.black[700])};
`;

const StyledBackButton = styled(IconButton)`
    padding: 0;
    border-radius: 12px;
    height: 40px;

    &:active {
        background-color: ${({ theme }) => theme.colors.black[50]};
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

interface Props {
    isExpanded: boolean;
    backRoute?: AppRoutes;
}

export const Header = observer(({ isExpanded, backRoute }: Props) => {
    const { store, saveArtifacts } = useAdvancedEditorContext();
    const [figmaModalOpen, setFigmaModalOpen] = useState(false);
    const figmaImporterEnabled = useFlag('figma_importer', false);
    const projectTemplateCreator = useFlag('project_template_creator', false);

    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);

    const handleOnClickHome = async () => {
        setBusy(true);
        try {
            await saveArtifacts(store);
        } catch (e) {
            logger.error('Failed to save artifacts on exit', e);
        }
        if (backRoute) {
            navigate(backRoute);
        } else {
            navigate(AppRoutes.VideoStudio);
        }
    };

    const validate = () => {
        const children = store.pages.map(page => page.children).flat() as ElementType[];

        const invalidElement = children.find((child: ElementType) => child.type === 'image' && child.src.includes('figma-alpha'));

        if (invalidElement) {
            store.selectElements([invalidElement.id]);
            alert('Selected element is invalid, please replace it with our image/shape/text element');
            return;
        }

        alert('No invalid elements found');
    };

    const showFigmaImportButton = figmaImporterEnabled || projectTemplateCreator;

    return (
        <Wrapper>
            {showFigmaImportButton && (
                <>
                    <button onClick={() => setFigmaModalOpen(true)} type="button">
                        Import Figma Template
                    </button>
                    <button onClick={validate}>Validate</button>
                    {figmaModalOpen && <FigmaModal onClose={() => setFigmaModalOpen(false)} />}
                </>
            )}

            {busy ? (
                <StyledBackButton>
                    <StyledCircularProgress size="16px" />
                </StyledBackButton>
            ) : (
                <StyledBackButton data-testid="video-back-button" onClick={handleOnClickHome}>
                    <StyledMaskedIcon src={ArrowLeft} size="32px" />
                </StyledBackButton>
            )}

            {isExpanded && (
                <HeaderWrapper>
                    <VideoTitleInput />
                    <VerticalDivider />
                    <CropDropdown />
                </HeaderWrapper>
            )}
        </Wrapper>
    );
});
